// src/components/homepage/HomePageRoadmap.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, Globe, ArrowRight } from 'lucide-react';
import {Button} from '@dopomogai/ui';
import clsx from 'clsx'; // Import clsx

// ... (Interfaces remain the same) ...
interface RoadmapData {
  headline: string;
  intro: string;
  phase1_title: string;
  phase1_status: string;
  phase1_item1: string;
  phase1_item2: string;
  phase1_item3: string;
  phase2_title: string;
  phase2_status: string;
  phase2_item1: string;
  phase2_item2: string;
  phase2_item3: string;
  phase3_title: string;
  phase3_status: string;
  phase3_item1: string;
  phase3_item2: string;
  phase3_item3: string;
  cta: string;
}

interface HomePageRoadmapProps {
  roadmapData: RoadmapData;
  onAction: (actionType: string, payload?: any) => void;
  id?: string;
}


// --- REFINED PhaseCard with internal hover animation ---
const PhaseCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  status: string;
  items: string[];
  statusColor: string;
  borderColor?: string; // Optional border color for emphasis
}> = ({ icon, title, status, items, statusColor, borderColor }) => {
  return (
    // The outer div is a "group" and maintains its place in the grid. No motion here.
    <div className="relative h-full group">
      {/* This is the black background that will act as the shadow */}
      <div className={clsx(
        "absolute inset-0 bg-black rounded-xl transition-transform duration-200 ease-out",
        "group-hover:translate-x-1.5 group-hover:translate-y-1.5"
      )} />

      {/* This is the visible card content that lifts up */}
      <div className={clsx(
        "relative bg-white p-6 rounded-xl border-2 flex flex-col h-full transition-transform duration-200 ease-out",
        "group-hover:-translate-x-1 group-hover:-translate-y-1",
        borderColor ? borderColor : "border-black"
      )}>
        <div className="flex items-center gap-4 mb-4">
          <div className="p-2 bg-slate-100 border-2 border-black rounded-lg">{icon}</div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <p className={`text-sm font-semibold ${statusColor}`}>{status}</p>
          </div>
        </div>
        <ul className="space-y-2.5 text-slate-600 flex-grow">
          {items.map((item, index) => (
            <li key={index} className="flex items-start gap-2.5">
              <CheckCircle size={16} className="text-emerald-500 mt-1 shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};


export const HomePageRoadmap: React.FC<HomePageRoadmapProps> = ({ roadmapData, onAction, id }) => {

  const phases = [
    {
      icon: <CheckCircle size={24} className="text-emerald-600" />,
      title: roadmapData.phase1_title,
      status: roadmapData.phase1_status,
      statusColor: 'text-emerald-600',
      items: [roadmapData.phase1_item1, roadmapData.phase1_item2, roadmapData.phase1_item3],
      borderColor: 'border-black',
    },
    {
      icon: <Clock size={24} className="text-sky-600" />,
      title: roadmapData.phase2_title,
      status: roadmapData.phase2_status,
      statusColor: 'text-sky-600',
      items: [roadmapData.phase2_item1, roadmapData.phase2_item2, roadmapData.phase2_item3],
      borderColor: 'border-blue-500', // Matching image: emphasized middle card
    },
    {
      icon: <Globe size={24} className="text-purple-600" />,
      title: roadmapData.phase3_title,
      status: roadmapData.phase3_status,
      statusColor: 'text-purple-600',
      items: [roadmapData.phase3_item1, roadmapData.phase3_item2, roadmapData.phase3_item3],
      borderColor: 'border-black',
    },
  ];

  const gridVariants = { /* ... */ };
  const cardVariants = { /* ... */ };
  // No changes needed to variants

  return (
    <section id={id} className="bg-slate-100 py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="text-center"
        >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                {roadmapData.headline}
            </h2>
            <p className="mt-4 max-w-3xl mx-auto text-lg text-slate-600">
                {roadmapData.intro}
            </p>
        </motion.div>

        <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12"
            variants={gridVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
        >
            {phases.map((phase, index) => (
                <motion.div key={index} variants={cardVariants}>
                    {/* The PhaseCard now handles its own hover effect without framer-motion props */}
                    <PhaseCard {...phase} />
                </motion.div>
            ))}
        </motion.div>

        <div className="text-center mt-12">
            <Button
                text={roadmapData.cta}
                variant="primary"
                size="lg"
                onClick={() => onAction('NAVIGATE_TO_PAGE', { path: '/roadmap' })}
            />
        </div>
      </div>
    </section>
  );
};

